import React, { useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { withRouter, useHistory } from "react-router-dom";
import { FeatureFlag } from "../../components/FeatureFlag/FeatureFlag";
import { featureFlags } from "../../components/FeatureFlag/featureFlagConst";
import { editQuotePath, editOpportunityPath, viewPolicyPath, customerDetailsPath } from "../../router/paths";
import { getGlobalConfiguration } from "../../utils/getGlobalConfiguration";
import { CrmWidget } from "@tia/crm-widget";
import { isObject } from "../../utils/utility";

const isEventWithDetail = (event: Event): event is Event & { detail: Record<string, unknown> } => {
  if (!isObject(event) || !("detail" in event) || !isObject(event.detail)) {
    return false;
  }

  return true;
};

const isDetailWithPartyIdAndValue = (event: Event): event is Event & { detail: { partyId: number; value: string } } => {
  if (!isEventWithDetail(event)) {
    return false;
  }

  return "partyId" in event.detail && "value" in event.detail;
};

const Crm = () => {
  const history = useHistory();
  const openInNewTab = getGlobalConfiguration("crmWidget.relatedOpenInNewTab");
  const { i18n } = useTranslation();
  const [eventListenerRef, setEventListenerRef] = useState<HTMLDivElement | null>(null);

  const setDivRef = (node: HTMLDivElement) => {
    const handleQuoteClick = (event: Event) => {
      if (!isDetailWithPartyIdAndValue(event)) {
        return;
      }

      const { partyId, value } = event.detail;

      handleFunction({
        path: editQuotePath({ customerId: partyId.toString(), quoteId: value }),
        target: value,
      });
    };

    const handleOpportunityClick = (event: Event) => {
      if (!isDetailWithPartyIdAndValue(event)) {
        return;
      }

      const { partyId, value } = event.detail;

      handleFunction({ path: editOpportunityPath(partyId.toString(), value), target: value });
    };

    const handlePolicyClick = (event: Event) => {
      if (!isDetailWithPartyIdAndValue(event)) {
        return;
      }

      const { partyId, value } = event.detail;

      handleFunction({ path: viewPolicyPath(`${partyId}`, value), target: value });
    };

    const handleCustomerClick = (event: Event) => {
      if (isEventWithDetail(event) && "partyId" in event.detail && typeof event.detail.partyId === "number") {
        const { partyId } = event.detail;
        handleFunction({
          path: customerDetailsPath(partyId.toString()),
          target: `${partyId}`,
        });
      }
    };

    const handleFunction = ({ path, target }: { path: string | URL; target: string }) => {
      if (!openInNewTab) {
        history.push(path);
        return;
      }

      if (typeof path === "string" && path.charAt(0) === "/") {
        path = path.substring(1);
      }

      window.open(path, target);
    };

    if (node) {
      node.addEventListener("ds-crm-widget-detail-quote-clicked", handleQuoteClick);
      node.addEventListener("ds-crm-widget-detail-basket-clicked", handleOpportunityClick);
      node.addEventListener("ds-crm-widget-detail-opportunity-clicked", handleOpportunityClick);
      node.addEventListener("ds-crm-widget-detail-policy-clicked", handlePolicyClick);
      node.addEventListener("ds-crm-widget-detail-customer-clicked", handleCustomerClick);
    }

    if (eventListenerRef) {
      eventListenerRef.removeEventListener("ds-crm-widget-detail-quote-clicked", handleQuoteClick);
      eventListenerRef.removeEventListener("ds-crm-widget-detail-basket-clicked", handleOpportunityClick);
      eventListenerRef.removeEventListener("ds-crm-widget-detail-opportunity-clicked", handleOpportunityClick);
      eventListenerRef.removeEventListener("ds-crm-widget-detail-policy-clicked", handlePolicyClick);
      eventListenerRef.removeEventListener("ds-crm-widget-detail-customer-clicked", handleCustomerClick);
    }

    setEventListenerRef(node);
  };

  return (
    <FeatureFlag featureFlag={featureFlags.crm.crmView.crmWidget}>
      <div ref={setDivRef}>
        {eventListenerRef && (
          <CrmWidget eventListenerRef={eventListenerRef} translations={i18n.store.data[i18n.language]?.crm_widget} />
        )}
      </div>
    </FeatureFlag>
  );
};

export default withTranslation()(withRouter(Crm));
